import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
    Gem, 
    Sparkles, 
    ArrowUpCircle, 
    Trophy, 
    BookOpen, 
    Settings,
    LogOut,
    Menu,
    X,
    Flag,
    Construction,
    LayoutDashboard,
    Watch,
    Crown,
    Clock,
    Zap,
    Sun,
    Moon,
    Globe,
    LayoutGrid,
    List,
    Ticket
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSiteLock } from '../../context/SiteLockContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useView } from '../../context/ViewContext';
import { Button } from '../ui/button';
import { GlobalSearch } from '../search/GlobalSearch';
import { cn } from '../../lib/utils';

const navItems = [
    { path: '/', icon: LayoutDashboard, labelKey: 'dashboard', page: 'dashboard' },
    { path: '/meta', icon: Crown, label: 'Meta', page: 'meta' },
    { path: '/relics', icon: Gem, label: 'Relics', page: 'relics' },
    { path: '/accessories', icon: Watch, label: 'Accessories', page: 'accessories' },
    { path: '/skills', icon: Zap, label: 'Skills', page: 'skills' },
    { path: '/gachas', icon: Sparkles, label: 'Gachas', page: 'gachas' },
    { path: '/upgrades', icon: ArrowUpCircle, label: 'Upgrades', page: 'upgrades' },
    { path: '/achievements', icon: Trophy, label: 'Achievements', page: 'achievements' },
    { path: '/tutorials', icon: BookOpen, label: 'Tutorials', page: 'tutorials' },
    { path: '/timers', icon: Clock, labelKey: 'timers', page: 'timers' },
    { path: '/codes', icon: Ticket, labelKey: 'codes', page: 'codes' },
];

export const Sidebar = ({ isOpen, onToggle }) => {
    const { user, logout, isAdmin } = useAuth();
    const { isPageLocked } = useSiteLock();
    const { t, language, setLanguage } = useLanguage();
    const { isDark, toggleTheme } = useTheme();
    const { isGrid, toggleView } = useView();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <>
            {/* Mobile menu button */}
            <button 
                data-testid="mobile-menu-toggle"
                onClick={onToggle}
                className="lg:hidden fixed top-4 left-4 z-[60] p-2 rounded-lg bg-card border border-white/10 text-gray-400 hover:text-white dark:bg-card dark:border-white/10"
            >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Overlay for mobile */}
            {isOpen && (
                <div 
                    className="lg:hidden fixed inset-0 bg-black/60 z-40"
                    onClick={onToggle}
                />
            )}

            {/* Sidebar */}
            <aside 
                data-testid="sidebar"
                className={cn(
                    "fixed left-0 top-0 h-full w-64 z-50",
                    "bg-black/60 dark:bg-black/60 light:bg-white/90 backdrop-blur-xl border-r border-white/10 dark:border-white/10 light:border-gray-200",
                    "flex flex-col transition-transform duration-300",
                    "lg:translate-x-0",
                    isOpen ? "translate-x-0" : "-translate-x-full"
                )}
            >
                {/* Logo */}
                <div className="p-6 border-b border-white/10 dark:border-white/10 light:border-gray-200">
                    <h1 className="text-2xl font-bold tracking-wider text-white dark:text-white light:text-gray-900 font-['Rajdhani']">
                        <span className="text-cyan-400">SystemHunter</span> WIKI
                    </h1>
                </div>

                {/* Global Search */}
                <div className="px-4 pt-4 pb-2">
                    <GlobalSearch />
                </div>

                {/* Settings Row - Theme, Language, View */}
                <div className="px-4 py-2 border-b border-white/10 dark:border-white/10 light:border-gray-200">
                    <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 dark:bg-white/5 light:bg-gray-100 overflow-hidden">
                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            data-testid="theme-toggle"
                            className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-md hover:bg-white/10 dark:hover:bg-white/10 light:hover:bg-gray-200 text-gray-400 hover:text-white dark:hover:text-white light:hover:text-gray-900 transition-colors"
                            title={isDark ? t('lightMode') : t('darkMode')}
                        >
                            {isDark ? <Sun size={14} /> : <Moon size={14} />}
                            <span className="text-xs">{isDark ? 'Light' : 'Dark'}</span>
                        </button>

                        <div className="w-px h-5 bg-white/10 dark:bg-white/10 light:bg-gray-300 shrink-0"></div>

                        {/* Language Dropdown */}
                        <div className="flex items-center justify-center px-2">
                            <Globe size={12} className="text-gray-500 mr-1" />
                            <select
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                                data-testid="language-select"
                                className="bg-transparent border-none text-xs text-gray-400 dark:text-gray-400 light:text-gray-600 hover:text-white cursor-pointer focus:outline-none"
                            >
                                <option value="de" className="bg-gray-900">DE</option>
                                <option value="en" className="bg-gray-900">EN</option>
                            </select>
                        </div>

                        <div className="w-px h-5 bg-white/10 dark:bg-white/10 light:bg-gray-300 shrink-0"></div>

                        {/* View Toggle */}
                        <button
                            onClick={toggleView}
                            data-testid="view-toggle"
                            className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-md hover:bg-white/10 dark:hover:bg-white/10 light:hover:bg-gray-200 text-gray-400 hover:text-white dark:hover:text-white light:hover:text-gray-900 transition-colors"
                            title={isGrid ? t('listView') : t('gridView')}
                        >
                            {isGrid ? <List size={14} /> : <LayoutGrid size={14} />}
                            <span className="text-xs">{isGrid ? 'List' : 'Grid'}</span>
                        </button>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    {navItems.map((item, index) => {
                        const locked = isPageLocked(item.page);
                        const label = item.labelKey ? t(item.labelKey) : item.label;
                        return (
                            <div key={item.path} className="relative">
                                {locked ? (
                                    <div
                                        className={cn(
                                            "flex items-center gap-3 px-4 py-3 rounded-lg",
                                            "bg-red-950/40 border border-red-500/20",
                                            "cursor-not-allowed",
                                            "animate-fade-in opacity-0",
                                            `stagger-${index + 1}`
                                        )}
                                    >
                                        <item.icon size={20} className="text-red-400/50" />
                                        <span className="font-medium text-red-400/50 flex-1">{label}</span>
                                        <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-red-500/20 border border-red-500/30">
                                            <Construction size={12} className="text-red-400" />
                                            <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">Dev</span>
                                        </div>
                                    </div>
                                ) : (
                                    <NavLink
                                        to={item.path}
                                        data-testid={`nav-${label.toLowerCase()}`}
                                        onClick={() => onToggle && window.innerWidth < 1024 && onToggle()}
                                        className={({ isActive }) => cn(
                                            "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200",
                                            "text-gray-400 hover:bg-white/5 hover:text-white dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white light:text-gray-600 light:hover:bg-gray-100 light:hover:text-gray-900",
                                            "animate-fade-in opacity-0",
                                            `stagger-${index + 1}`,
                                            isActive && "bg-cyan-500/10 text-cyan-400 border-l-2 border-cyan-400 dark:bg-cyan-500/10 dark:text-cyan-400 light:bg-cyan-50 light:text-cyan-600"
                                        )}
                                    >
                                        <item.icon size={20} />
                                        <span className="font-medium">{label}</span>
                                    </NavLink>
                                )}
                            </div>
                        );
                    })}

                    {/* Admin Section */}
                    {isAdmin && (
                        <div className="pt-4 mt-4 border-t border-white/10 dark:border-white/10 light:border-gray-200 space-y-1">
                            <p className="px-4 py-2 text-xs text-gray-600 dark:text-gray-600 light:text-gray-400 uppercase tracking-wider">
                                Admin
                            </p>
                            
                            <NavLink
                                to="/admin"
                                data-testid="nav-admin"
                                onClick={() => onToggle && window.innerWidth < 1024 && onToggle()}
                                className={({ isActive }) => cn(
                                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200",
                                    "text-amber-400 hover:bg-amber-500/10 hover:text-amber-300",
                                    "animate-fade-in opacity-0 stagger-6",
                                    isActive && "bg-amber-500/10 border-l-2 border-amber-400"
                                )}
                            >
                                <Settings size={20} />
                                <span className="font-medium">{t('adminPanel')}</span>
                            </NavLink>

                            <NavLink
                                to="/reports"
                                data-testid="nav-reports"
                                onClick={() => onToggle && window.innerWidth < 1024 && onToggle()}
                                className={({ isActive }) => cn(
                                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200",
                                    "text-red-400 hover:bg-red-500/10 hover:text-red-300",
                                    "animate-fade-in opacity-0 stagger-6",
                                    isActive && "bg-red-500/10 border-l-2 border-red-400"
                                )}
                            >
                                <Flag size={20} />
                                <span className="font-medium">{t('reports')}</span>
                            </NavLink>
                        </div>
                    )}
                </nav>

                {/* User section */}
                <div className="p-4 border-t border-white/10 dark:border-white/10 light:border-gray-200">
                    <div className="flex items-center gap-3 mb-3">
                        {user?.avatar ? (
                            <img 
                                src={user.avatar} 
                                alt={user.username}
                                className="w-10 h-10 rounded-full border border-white/10"
                            />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                                <span className="text-cyan-400 font-bold">
                                    {user?.username?.charAt(0).toUpperCase()}
                                </span>
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="text-white dark:text-white light:text-gray-900 font-medium truncate">{user?.username}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 light:text-gray-400">
                                {isAdmin ? t('administrator') : t('member')}
                            </p>
                        </div>
                    </div>
                    <Button
                        data-testid="logout-button"
                        variant="ghost"
                        className="w-full justify-start text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                        onClick={handleLogout}
                    >
                        <LogOut size={18} className="mr-2" />
                        {t('logout')}
                    </Button>
                </div>
            </aside>
        </>
    );
};
